import React, { useState } from "react";
import { 
  FileText, Brain, MessageSquare, Languages, PenTool, 
  Sparkles, Upload, ArrowRight, ArrowLeft, Bot, BookOpen, 
  FileQuestion, Lightbulb, Wand2, Crown, PenLine, 
  X, ExternalLink 
} from "lucide-react";


const tools = [
  {
    id: "pdf-quiz",
    title: "PDF to Quiz",
    description: "Upload a PDF and generate quiz questions automatically to test your knowledge.",
    icon: FileQuestion,
    color: "bg-secondary/15 text-secondary",
    cta: "Generate Quiz",
  },
  {
    id: "ai-summarizer",
    title: "AI Summarizer",
    description: "Get concise summaries of long documents, textbooks, or lecture notes instantly.",
    icon: FileText,
    color: "bg-primary/10 text-primary",
    cta: "Summarize",
  },
  {
    id: "ai-chat",
    title: "Ask AI",
    description: "Chat with AI about any topic — get explanations, solve problems, or brainstorm ideas.",
    icon: MessageSquare,
    color: "bg-chart-5/10 text-chart-5",
    cta: "Start Chat",
  },
  {
    id: "ai-explain",
    title: "Explain Like I'm 5",
    description: "Paste any complex concept and get a simple, easy-to-understand explanation.",
    icon: Lightbulb,
    color: "bg-chart-4/10 text-chart-4",
    cta: "Explain",
  },
  {
    id: "ai-translate",
    title: "Translator",
    description: "Translate academic texts between Azerbaijani, English, Russian, and Turkish.",
    icon: Languages,
    color: "bg-secondary/15 text-secondary",
    cta: "Translate",
  },
  {
    id: "ai-writer",
    title: "Essay Helper",
    description: "Get help structuring essays, improving grammar, and refining academic writing.",
    icon: PenTool,
    color: "bg-primary/10 text-primary",
    cta: "Write",
  },
  {
    id: "handwriting-pdf",
    title: "Handwriting to PDF",
    description: "Scan or upload handwritten notes and convert them into clean, searchable PDF documents.",
    icon: PenLine,
    color: "bg-chart-5/10 text-chart-5",
    cta: "Convert",
  },
];

const premiumTools = [
  {
    id: "ai-tutor",
    title: "Personal AI Tutor",
    description: "1-on-1 AI tutoring sessions with adaptive learning paths tailored to your progress.",
    icon: Brain,
    color: "bg-chart-4/10 text-chart-4",
    cta: "Start Session",
  },
  {
    id: "ai-plagiarism",
    title: "Plagiarism Checker",
    description: "Check your essays and papers for plagiarism with detailed similarity reports.",
    icon: FileText,
    color: "bg-primary/10 text-primary",
    cta: "Check Now",
  },
  {
    id: "ai-presentation",
    title: "Slide Generator",
    description: "Generate professional presentation slides from your notes or documents automatically.",
    icon: Wand2,
    color: "bg-secondary/15 text-secondary",
    cta: "Generate Slides",
  },
];

const socialLinks = [
  { name: "YouTube", username: "@yeniders.az", icon: "▶", color: "bg-destructive/10 text-destructive", hoverBg: "hover:bg-destructive/15", url: "https://youtube.com/@yeniders.az" },
  { name: "Instagram", username: "@yeniders.az", icon: "📷", color: "bg-chart-5/10 text-chart-5", hoverBg: "hover:bg-chart-5/15", url: "https://instagram.com/yeniders.az" },
  { name: "WhatsApp", username: "@yeniders.az", icon: "💬", color: "bg-secondary/10 text-secondary", hoverBg: "hover:bg-secondary/15", url: "https://chat.whatsapp.com" },
  { name: "LinkedIn", username: "@yeniders.az", icon: "in", color: "bg-primary/10 text-primary", hoverBg: "hover:bg-primary/15", url: "https://linkedin.com/company/yeniders" },
];

const AiTools = () => {
  const [activeTool, setActiveTool] = useState(null);
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showSocialPopup, setShowSocialPopup] = useState(false);

  const active = tools.find((t) => t.id === activeTool) || premiumTools.find((t) => t.id === activeTool);
  // const isPremiumTool = premiumTools.some(t => t.id === activeTool);

  const handleRun = () => {
    if (!inputText.trim() && !uploadedFile) return;
    setIsLoading(true);
    // Simulate AI response for now
    setTimeout(() => {
      setOutputText(
        activeTool === "pdf-quiz"
          ? "📝 Generated Quiz Questions:\n\n1. What is the main concept discussed in Chapter 3?\n   a) Thermodynamics\n   b) Kinematics ✓\n   c) Optics\n   d) Magnetism\n\n2. Which formula represents Newton's second law?\n   a) F = ma ✓\n   b) E = mc²\n   c) V = IR\n   d) PV = nRT\n\n3. What is the SI unit of force?\n   a) Joule\n   b) Watt\n   c) Newton ✓\n   d) Pascal"
          : activeTool === "ai-summarizer"
          ? "📋 Summary:\n\nThe document covers three main topics: (1) fundamental principles of mechanics including Newton's laws and conservation of energy, (2) applications in real-world engineering problems, and (3) advanced problem-solving techniques for university-level physics. Key takeaways include the relationship between force and acceleration, work-energy theorem applications, and momentum conservation in collisions."
          : activeTool === "ai-explain"
          ? "🧒 Simple Explanation:\n\nImagine you're pushing a toy car. The harder you push (more force), the faster it goes (more acceleration). But if the car is really heavy (more mass), you need to push harder to make it go the same speed. That's basically what F = ma means — Force equals mass times acceleration!"
          : activeTool === "ai-translate"
          ? "🌐 Translation:\n\n(AZ) Kvant mexanikası atomaltı hissəciklərin davranışını öyrənən fizika sahəsidir.\n\n(EN) Quantum mechanics is a branch of physics that studies the behavior of subatomic particles.\n\n(RU) Квантовая механика — это раздел физики, изучающий поведение субатомных частиц."
          : activeTool === "ai-writer"
          ? "✍️ Improved Essay:\n\nIntroduction (Revised):\n\nThe rapid advancement of artificial intelligence has fundamentally transformed the educational landscape, presenting both unprecedented opportunities and significant challenges for modern learners. This essay examines three critical dimensions of AI integration in higher education: personalized learning pathways, automated assessment systems, and the ethical implications of AI-driven academic tools."
          : "🤖 AI Response:\n\nBased on your question, here's a detailed explanation...\n\nThe concept you're asking about relates to fundamental principles in this field. Let me break it down step by step:\n\n1. First, understand the basic definition...\n2. Then, consider how it applies in practice...\n3. Finally, note the key relationships between variables.\n\nWould you like me to elaborate on any specific part?"
      );
      setIsLoading(false);
    }, 1500);
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 py-6 animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <h1 className="text-[28px] font-display font-bold text-foreground">AI Tools</h1>
        </div>
        <p className="text-sm text-muted-foreground">Supercharge your learning with AI-powered tools</p>
      </div>

      {!activeTool ? (
        /* Tool Grid */
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tools.map((tool) => (
                <button
                  onClick={() => { setActiveTool(tool.id); setOutputText(""); setInputText(""); setUploadedFile(null); }}
                  className="w-full stat-card !p-5 text-left group border-none cursor-pointer bg-card hover:bg-muted transition-all"
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${tool.color}`}>
                    <tool.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-display font-bold text-foreground mb-1 group-hover:text-secondary transition-colors">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">{tool.description}</p>
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-secondary">
                    {tool.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
            ))}

            {/* Follow Us Card - in free section */}
            <button
              onClick={() => setShowSocialPopup(true)}
              className="stat-card !p-5 text-left group relative overflow-hidden border-dashed border-border border-2 bg-transparent hover:bg-muted/30 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 bg-accent text-accent-foreground">
                <ExternalLink className="w-6 h-6" />
              </div>
              <h3 className="text-base font-display font-bold text-foreground mb-1 group-hover:text-secondary transition-colors">
                Follow Us
              </h3>
              <p className="text-sm text-muted-foreground mb-4">Free resources, tips & membership benefits on our social media channels.</p>
              <span className="flex items-center gap-2 text-sm font-semibold text-secondary">
                <span className="flex gap-1">
                  {socialLinks.map((s) => (
                    <span key={s.name} className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold ${s.color}`}>
                      {s.icon}
                    </span>
                  ))}
                </span>
                Join <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>

          {/* Separator */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="flex items-center gap-1.5 text-xs font-bold text-chart-4 uppercase tracking-wider">
              <Crown className="w-3.5 h-3.5" /> Premium
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {premiumTools.map((tool) => (
                <button
                  onClick={() => { setActiveTool(tool.id); setOutputText(""); setInputText(""); setUploadedFile(null); }}
                  className="w-full stat-card !p-5 text-left group relative overflow-hidden border-none cursor-pointer bg-card hover:bg-muted transition-all"
                >
                  <div className="absolute top-3 right-3">
                    <Crown className="w-4 h-4 text-chart-4" />
                  </div>
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${tool.color}`}>
                    <tool.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-display font-bold text-foreground mb-1 group-hover:text-secondary transition-colors">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">{tool.description}</p>
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-chart-4">
                    {tool.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
            ))}
          </div>
        </div>
      ) : (
        /* Active Tool View */
        <div>
          <button onClick={() => { setActiveTool(null); setOutputText(""); setInputText(""); }}
            className="mb-6 group text-sm font-medium text-muted-foreground hover:text-foreground transition-all flex items-center gap-2 bg-transparent border-none cursor-pointer">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-secondary/10 group-hover:text-secondary transition-all">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            </div>
            Back to all tools
          </button>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Input Panel */}
            <div className="flex-1 min-w-0 space-y-4">
              <div className="stat-card !p-5 bg-card">
                <div className="flex items-center gap-3 mb-4">
                  {active && (
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active.color}`}>
                      <active.icon className="w-5 h-5" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-display font-bold text-foreground">{active?.title}</h2>
                    <p className="text-xs text-muted-foreground">{active?.description}</p>
                  </div>
                </div>

                {/* File Upload (for PDF tools) */}
                {(activeTool === "pdf-quiz" || activeTool === "ai-summarizer") && (
                  <div className="mb-4">
                    <label className="block w-full p-6 rounded-2xl border-2 border-dashed border-border hover:border-secondary/50 transition-colors cursor-pointer text-center">
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-medium text-foreground">
                        {uploadedFile || "Drop a PDF here or click to upload"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">PDF, DOC up to 20MB</p>
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx"
                        onChange={(e) => setUploadedFile(e.target.files?.[0]?.name || null)} />
                    </label>
                  </div>
                )}

                {/* Text Input */}
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={
                    activeTool === "pdf-quiz" ? "Or paste text here to generate quiz questions..."
                      : activeTool === "ai-summarizer" ? "Or paste the text you want to summarize..."
                      : activeTool === "ai-chat" ? "Ask me anything..."
                      : activeTool === "ai-explain" ? "Paste a complex concept to simplify..."
                      : activeTool === "ai-translate" ? "Enter text to translate..."
                      : "Enter your essay or text to improve..."
                  }
                  className="w-full min-h-[160px] p-4 rounded-2xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-secondary/50"
                />

                {/* Language selector for translator */}
                {activeTool === "ai-translate" && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-muted-foreground">Translate to:</span>
                    {["English", "Azərbaycan", "Русский", "Türkçe"].map((lang) => (
                      <button key={lang} className="text-xs px-3 py-1.5 rounded-xl bg-muted text-muted-foreground hover:bg-secondary/10 hover:text-secondary transition-colors font-medium border-none cursor-pointer">
                        {lang}
                      </button>
                    ))}
                  </div>
                )}

                <button onClick={handleRun} disabled={isLoading || (!inputText.trim() && !uploadedFile)}
                  className="mt-4 w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 border-none cursor-pointer">
                  {isLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      {active?.cta}
                    </>
                  )}
                </button>
              </div>

              {/* Output */}
              {outputText && (
                <div className="stat-card !p-5 animate-in fade-in slide-in-from-bottom-2 duration-300 bg-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="w-4 h-4 text-secondary" />
                    <h3 className="text-sm font-display font-bold text-foreground">Result</h3>
                  </div>
                  <pre className="text-sm text-foreground whitespace-pre-wrap leading-relaxed font-sans">{outputText}</pre>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
                    <button className="px-3 py-1.5 rounded-xl text-xs font-medium bg-muted text-muted-foreground hover:text-foreground transition-colors border-none cursor-pointer">
                      Copy
                    </button>
                    <button className="px-3 py-1.5 rounded-xl text-xs font-medium bg-muted text-muted-foreground hover:text-foreground transition-colors border-none cursor-pointer">
                      Save to My Books
                    </button>
                    <button className="px-3 py-1.5 rounded-xl text-xs font-medium bg-muted text-muted-foreground hover:text-foreground transition-colors border-none cursor-pointer">
                      Share
                    </button>
                    <button onClick={handleRun} className="ml-auto px-3 py-1.5 rounded-xl text-xs font-medium bg-secondary/15 text-secondary hover:bg-secondary/20 transition-colors border-none cursor-pointer">
                      Regenerate
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Tips Panel */}
            <div className="lg:w-72 flex-shrink-0">
              <div className="sticky top-20 space-y-4">
                <div className="stat-card !p-4 bg-card">
                  <h3 className="text-sm font-display font-bold text-foreground mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-secondary" /> Tips
                  </h3>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    {activeTool === "pdf-quiz" && (
                      <>
                        <p>• Upload a lecture PDF for best results</p>
                        <p>• Quiz covers key concepts automatically</p>
                        <p>• Edit questions after generation</p>
                        <p>• Save quizzes to My Books</p>
                      </>
                    )}
                    {activeTool === "ai-summarizer" && (
                      <>
                        <p>• Works best with structured documents</p>
                        <p>• Summaries highlight key points</p>
                        <p>• Try different text lengths</p>
                      </>
                    )}
                    {activeTool === "ai-chat" && (
                      <>
                        <p>• Ask follow-up questions for clarity</p>
                        <p>• Be specific about your topic</p>
                        <p>• Mention your course for context</p>
                      </>
                    )}
                    {activeTool === "ai-explain" && (
                      <>
                        <p>• Paste formulas, definitions, or concepts</p>
                        <p>• Get analogies and real-world examples</p>
                        <p>• Great for exam preparation</p>
                      </>
                    )}
                    {activeTool === "ai-translate" && (
                      <>
                        <p>• Supports academic terminology</p>
                        <p>• Preserves formatting and structure</p>
                        <p>• Ideal for multilingual study materials</p>
                      </>
                    )}
                    {activeTool === "ai-writer" && (
                      <>
                        <p>• Paste your draft for improvements</p>
                        <p>• Get structure suggestions</p>
                        <p>• Academic tone and citation help</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="stat-card !p-4 bg-card">
                  <h3 className="text-sm font-display font-bold text-foreground mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-secondary" /> Recent
                  </h3>
                  <div className="space-y-1.5">
                    <div className="p-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                      <p className="text-xs font-medium text-foreground truncate">Calculus II Summary</p>
                      <p className="text-[10px] text-muted-foreground">Summarizer · 2h ago</p>
                    </div>
                    <div className="p-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                      <p className="text-xs font-medium text-foreground truncate">Physics Quiz — Ch.3</p>
                      <p className="text-[10px] text-muted-foreground">PDF to Quiz · 1d ago</p>
                    </div>
                    <div className="p-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                      <p className="text-xs font-medium text-foreground truncate">Thermodynamics Explained</p>
                      <p className="text-[10px] text-muted-foreground">Explain · 3d ago</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Popup */}
      {showSocialPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowSocialPopup(false)} />
          <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-display font-bold text-foreground">Join Our Community</h3>
              <button onClick={() => setShowSocialPopup(false)} className="w-8 h-8 rounded-xl hover:bg-muted flex items-center justify-center transition-colors border-none cursor-pointer bg-transparent">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-xs text-muted-foreground mb-4">Follow us for free resources, tips & membership benefits.</p>
              <div className="grid grid-cols-4 gap-3">
                {socialLinks.map((link, i) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl ${link.hoverBg} transition-all duration-300 group`}
                  >
                    <span className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold ${link.color} group-hover:scale-110 transition-transform duration-300`}>
                      {link.icon}
                    </span>
                    <span className="text-[11px] font-semibold text-foreground">{link.name}</span>
                    <span className="text-[10px] text-muted-foreground">{link.username}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiTools;
